import CollaborationStore from "@/store/collaboration.store";
import { useCallback } from "react";
import { message } from "antd";
import { syncEditRecord, syncModifySpeakerName } from "@/services/record";
import { IEditRecordParams, IMergeRecordParams } from "@/types/record";
import { IPG } from "@/types/summary";
import ScrollableContent from "../ScrollableContent";
import deviceStore from "@/store/device.store";

interface SrcContentProps {
  streamName: string;
  className?: string;
}

const SrcContent = ({ streamName, className }: SrcContentProps) => {
  const srcContent = CollaborationStore((state) => state.srcContent);
  const unstableText = CollaborationStore((state) => state.unstableText);
  const unstableTranslateText = CollaborationStore((state) => state.unstableTranslateText);
  const displayMode = deviceStore((state) => state.displayMode.mode);

  // 封装编辑记录方法
  const handleEditRecord = useCallback(
    async (params: IEditRecordParams) => {
      const { modifiedParas, deletedParas } = params;
      try {
        await syncEditRecord({
          streamName,
          optEventType: "UPDATE",
          editNote: {
            pg: modifiedParas.map((item) => ({
              ...item,
              roleName: item.speakerName[0],
            })),
          },
        });
        if (deletedParas.length > 0) {
          await syncEditRecord({
            streamName,
            optEventType: "DELETE",
            pis: deletedParas.map((item) => item.pi),
            isTrans: false,
          });
        }
      } catch (error) {
        console.log("handleEditRecord error:", error);
      }
    },
    [streamName]
  );

  // 封装修改发言人
  const handleModifySpeakerName = useCallback(
    async (updatedParas: IPG[], editRoleType: "ONE" | "BATCH") => {
      try {
        await syncModifySpeakerName({
          streamName,
          pis: updatedParas.map((item) => item.pi),
          role: updatedParas[0].role,
          roleName: updatedParas[0].speakerName[0],
          editRoleType,
        });
        if (editRoleType === "BATCH") {
          message.success("发言人设置，应用到全局成功");
        }
      } catch (error) {
        console.log("syncEditRecord error:", error);
      }
    },
    [streamName]
  );

  const handleMergeRecord = async (params: IMergeRecordParams) => {
    const { modifiedParas, deletedParas } = params;
    try {
      if (modifiedParas.length > 0) {
        // 更新段落
        await syncEditRecord({
          streamName,
          optEventType: "UPDATE",
          editNote: {
            pg: modifiedParas.map((item) => ({
              ...item,
              roleName: item.speakerName[0],
            })),
          },
        });
      }
      if (deletedParas.length > 0) {
        // 删除段落
        await syncEditRecord({
          streamName,
          optEventType: "DELETE",
          pis: deletedParas.map((item) => item.pi),
          isTrans: false,
        });
      }
    } catch (error) {
      console.log("handleMergeRecord error:", error);
    }
  };

  const handleSpeakerPopoverChange = useCallback((visible: boolean) => {
    // 发言人弹窗状态变化处理
    void visible; // 防止linter警告
    // 这里可以根据需要添加额外的逻辑
  }, []);

  // 渲染原文内容
  const renderContent = useCallback((isTrans: boolean) => {
    return (
      <ScrollableContent
        data={srcContent}
        unstableText={isTrans ? unstableTranslateText : unstableText}
        isTrans={isTrans}
        onEditRecord={handleEditRecord}
        onModifySpeakerName={handleModifySpeakerName}
        onMergeRecord={handleMergeRecord}
        onSpeakerPopoverChange={handleSpeakerPopoverChange}
        className={`flex-1 ${displayMode === 'both' ? isTrans ? "h-[calc(50%-28px)] pt-7" : "h-[calc(50%-28px)] pb-7 pt-0.25" : "h-[calc(100%-48px)]"
          }`}
      />
    );
  }, [srcContent, unstableText, displayMode, handleEditRecord, handleModifySpeakerName, handleMergeRecord, handleSpeakerPopoverChange]);

  return (
    <div className={`${className} flex-1 overflow-hidden flex flex-col w-full`}>
      {/* 原文区域 */}
      {displayMode !== 'translation' && renderContent(false)}

      {/* 分割线 */}
      {displayMode === 'both' && (
        <div
          className="mx-2"
          style={{
            height: '1px',
            background: 'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 15%, rgba(0, 0, 0, 0.1) 85%, rgba(0, 0, 0, 0) 100%)'
          }}
        />
      )}

      {/* 译文区域 */}
      {displayMode !== 'original' && renderContent(true)}
      <div className='h-6'></div>
    </div>
  );
};

export default SrcContent;
